import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'robot-service-detail',
  templateUrl: './robot-service-detail.component.html',
  styleUrls: ['./robot-service-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    I18nPipe,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzDividerModule,
    NzSelectModule,
    NzIconModule,
    NzSpinModule,
    TestIdDirective
  ]
})
export class RobotServiceDetailComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject(NzModalRef);
  private readonly nzModalData: any = inject(NZ_MODAL_DATA);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  // 表单
  robotForm: FormGroup;

  // 数据加载和提交状态
  loading = false;
  submitting = false;

  // 编辑模式标志
  isEdit = false;
  robotId: string | null = null;

  // 语言选项
  languageOptions = [
    { label: this.i18n.fanyi('robot-service.language.zh-tw'), value: 'ZH_TW' },
    { label: this.i18n.fanyi('robot-service.language.zh-cn'), value: 'ZH_CN' },
    { label: this.i18n.fanyi('robot-service.language.en-us'), value: 'EN_US' }
  ];

  // 类型选项
  typeOptions = [
    { label: this.i18n.fanyi('robot-service.type.gpt'), value: 'Gpt' },
    { label: this.i18n.fanyi('robot-service.type.cbm'), value: 'Cbm' }
  ];

  identityTypeOptions = [
    { label: this.i18n.fanyi('robot-service.identity.virtual-persona'), value: 'VirtualPersona' },
    { label: this.i18n.fanyi('robot-service.identity.virtual-employee'), value: 'VirtualEmployee' }
  ];

  defaultOptions = [
    { label: this.i18n.fanyi('robot-service.default.yes'), value: true },
    { label: this.i18n.fanyi('robot-service.default.no'), value: false }
  ];

  constructor() {
    this.robotForm = this.fb.group({
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      url: ['', [Validators.required]],
      language: ['ZH_TW', [Validators.required]],
      type: ['Gpt', [Validators.required]],
      identityType: ['VirtualPersona', [Validators.required]],
      isDefault: [false, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.robotId = this.nzModalData.robotId;
    if (this.robotId) {
      this.isEdit = true;
      this.loadRobotDetail();
    }
  }

  // 加载机器人详情
  loadRobotDetail(): void {
    if (!this.robotId) return;

    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_ROBOT_SERVICE_DETAIL}/${this.robotId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          this.loading = false;
          if (res.success && res.data) {
            this.robotForm.patchValue(res.data);
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('robot-service.load-detail-failed'));
          }
        },
        error: err => {
          this.loading = false;
          this.message.error(this.i18n.fanyi('robot-service.load-detail-failed'));
          console.error('Load robot service detail error:', err);
        }
      });
  }

  // 保存机器人服务
  saveRobot(): void {
    if (this.robotForm.invalid) {
      // 标记所有表单控件为已触摸，以显示验证错误
      Object.values(this.robotForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      return;
    }

    this.submitting = true;
    const formData = this.robotForm.value;
    if (this.isEdit) {
      formData.id = this.robotId;
    }

    this.http
      .post(ConstantUtil.TENANT_ROBOT_SERVICE_UPDATE, formData)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(
              this.isEdit ? this.i18n.fanyi('robot-service.update-success') : this.i18n.fanyi('robot-service.create-success')
            );
            this.modalRef.close(res.data ?? true);
          } else {
            this.message.error(
              res.msg ?? (this.isEdit ? this.i18n.fanyi('robot-service.update-failed') : this.i18n.fanyi('robot-service.create-failed'))
            );
          }
        },
        error: err => {
          this.message.error(
            this.isEdit ? this.i18n.fanyi('robot-service.update-failed-retry') : this.i18n.fanyi('robot-service.create-failed-retry')
          );
          console.error(this.isEdit ? 'Update robot service failed:' : 'Create robot service failed:', err);
        }
      });
  }

  // 取消
  cancel(): void {
    this.modalRef.close();
  }
}
